"use strict";
// Copyright (c) 2023, Circle Technologies, LLC. All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
Object.defineProperty(exports, "__esModule", { value: true });
exports.SocialLoginProvider = exports.ErrorCode = exports.QuestionType = exports.ChallengeStatus = exports.ChallengeType = void 0;
// Enums
/**
 * ChallengeType.
 */
var ChallengeType;
(function (ChallengeType) {
    ChallengeType["INITIALIZE"] = "INITIALIZE";
    ChallengeType["SET_PIN"] = "SET_PIN";
    ChallengeType["CHANGE_PIN"] = "CHANGE_PIN";
    ChallengeType["RESTORE_PIN"] = "RESTORE_PIN";
    ChallengeType["SET_SECURITY_QUESTIONS"] = "SET_SECURITY_QUESTIONS";
    ChallengeType["CREATE_WALLET"] = "CREATE_WALLET";
    ChallengeType["CREATE_TRANSACTION"] = "CREATE_TRANSACTION";
    ChallengeType["ACCELERATE_TRANSACTION"] = "ACCELERATE_TRANSACTION";
    ChallengeType["CANCEL_TRANSACTION"] = "CANCEL_TRANSACTION";
    ChallengeType["SIGN_MESSAGE"] = "SIGN_MESSAGE";
    ChallengeType["SIGN_TYPEDDATA"] = "SIGN_TYPEDDATA";
    ChallengeType["SIGN_TRANSACTION"] = "SIGN_TRANSACTION";
    ChallengeType["UNKNOWN"] = "UNKNOWN";
})(ChallengeType = exports.ChallengeType || (exports.ChallengeType = {}));
/**
 * ChallengeStatus.
 */
var ChallengeStatus;
(function (ChallengeStatus) {
    ChallengeStatus["COMPLETE"] = "COMPLETE";
    ChallengeStatus["EXPIRED"] = "EXPIRED";
    ChallengeStatus["FAILED"] = "FAILED";
    ChallengeStatus["IN_PROGRESS"] = "IN_PROGRESS";
    ChallengeStatus["PENDING"] = "PENDING";
})(ChallengeStatus = exports.ChallengeStatus || (exports.ChallengeStatus = {}));
/**
 * Security question type.
 */
var QuestionType;
(function (QuestionType) {
    QuestionType["DATE"] = "DATE";
    QuestionType["TEXT"] = "TEXT";
})(QuestionType = exports.QuestionType || (exports.QuestionType = {}));
/**
 * Error code.
 */
var ErrorCode;
(function (ErrorCode) {
    ErrorCode[ErrorCode["unknown"] = -1] = "unknown";
    ErrorCode[ErrorCode["success"] = 0] = "success";
    ErrorCode[ErrorCode["apiParameterMissing"] = 1] = "apiParameterMissing";
    ErrorCode[ErrorCode["apiParameterInvalid"] = 2] = "apiParameterInvalid";
    ErrorCode[ErrorCode["forbidden"] = 3] = "forbidden";
    ErrorCode[ErrorCode["unauthorized"] = 4] = "unauthorized";
    ErrorCode[ErrorCode["retry"] = 9] = "retry";
    ErrorCode[ErrorCode["customerSuspended"] = 10] = "customerSuspended";
    ErrorCode[ErrorCode["pending"] = 11] = "pending";
    ErrorCode[ErrorCode["invalidSession"] = 12] = "invalidSession";
    ErrorCode[ErrorCode["invalidPartnerId"] = 13] = "invalidPartnerId";
    ErrorCode[ErrorCode["invalidMessage"] = 14] = "invalidMessage";
    ErrorCode[ErrorCode["invalidPhone"] = 15] = "invalidPhone";
    ErrorCode[ErrorCode["userAlreadyExisted"] = 155101] = "userAlreadyExisted";
    ErrorCode[ErrorCode["userNotFound"] = 155102] = "userNotFound";
    ErrorCode[ErrorCode["userTokenNotFound"] = 155103] = "userTokenNotFound";
    ErrorCode[ErrorCode["userTokenExpired"] = 155104] = "userTokenExpired";
    ErrorCode[ErrorCode["invalidUserToken"] = 155105] = "invalidUserToken";
    ErrorCode[ErrorCode["userWasInitialized"] = 155106] = "userWasInitialized";
    ErrorCode[ErrorCode["userHasSetPin"] = 155107] = "userHasSetPin";
    ErrorCode[ErrorCode["userHasSetSecurityQuestion"] = 155108] = "userHasSetSecurityQuestion";
    ErrorCode[ErrorCode["userWasDisabled"] = 155109] = "userWasDisabled";
    ErrorCode[ErrorCode["userDoesNotSetPinYet"] = 155110] = "userDoesNotSetPinYet";
    ErrorCode[ErrorCode["userDoesNotSetSecurityQuestionYet"] = 155111] = "userDoesNotSetSecurityQuestionYet";
    ErrorCode[ErrorCode["incorrectUserPin"] = 155112] = "incorrectUserPin";
    ErrorCode[ErrorCode["incorrectDeviceId"] = 155113] = "incorrectDeviceId";
    ErrorCode[ErrorCode["incorrectAppId"] = 155114] = "incorrectAppId";
    ErrorCode[ErrorCode["incorrectSecurityAnswers"] = 155115] = "incorrectSecurityAnswers";
    ErrorCode[ErrorCode["invalidChallengeId"] = 155116] = "invalidChallengeId";
    ErrorCode[ErrorCode["invalidApproveContent"] = 155117] = "invalidApproveContent";
    ErrorCode[ErrorCode["invalidEncryptionKey"] = 155118] = "invalidEncryptionKey";
    ErrorCode[ErrorCode["userPinLocked"] = 155119] = "userPinLocked";
    ErrorCode[ErrorCode["securityAnswersLocked"] = 155120] = "securityAnswersLocked";
    ErrorCode[ErrorCode["userOTPTokenExpiredError"] = 155130] = "userOTPTokenExpiredError";
    ErrorCode[ErrorCode["userOTPTokenInvalidError"] = 155131] = "userOTPTokenInvalidError";
    ErrorCode[ErrorCode["userOTPNotFoundError"] = 155132] = "userOTPNotFoundError";
    ErrorCode[ErrorCode["userOTPInvalidError"] = 155133] = "userOTPInvalidError";
    ErrorCode[ErrorCode["userOTPNotMatchError"] = 155134] = "userOTPNotMatchError";
    ErrorCode[ErrorCode["userEmailInvalidError"] = 155135] = "userEmailInvalidError";
    ErrorCode[ErrorCode["userEmailMismatchError"] = 155136] = "userEmailMismatchError";
    ErrorCode[ErrorCode["deviceIDInvalidError"] = 155137] = "deviceIDInvalidError";
    ErrorCode[ErrorCode["emailSendingFailedError"] = 155138] = "emailSendingFailedError";
    ErrorCode[ErrorCode["socialLoginTokenExpiredError"] = 155139] = "socialLoginTokenExpiredError";
    ErrorCode[ErrorCode["socialLoginProviderAppIDNotMatchError"] = 155140] = "socialLoginProviderAppIDNotMatchError";
    ErrorCode[ErrorCode["userOTPIsLockedError"] = 155141] = "userOTPIsLockedError";
    ErrorCode[ErrorCode["userOTPSendCountsOverLimitError"] = 155142] = "userOTPSendCountsOverLimitError";
    ErrorCode[ErrorCode["deviceTokenExpiredError"] = 155143] = "deviceTokenExpiredError";
    ErrorCode[ErrorCode["deviceTokenInvalidError"] = 155144] = "deviceTokenInvalidError";
    ErrorCode[ErrorCode["deviceTokenNotFoundError"] = 155145] = "deviceTokenNotFoundError";
    ErrorCode[ErrorCode["notEnoughFunds"] = 155201] = "notEnoughFunds";
    ErrorCode[ErrorCode["notEnoughBalance"] = 155202] = "notEnoughBalance";
    ErrorCode[ErrorCode["exceedWithdrawLimit"] = 155203] = "exceedWithdrawLimit";
    ErrorCode[ErrorCode["minimumFundsRequired"] = 155204] = "minimumFundsRequired";
    ErrorCode[ErrorCode["invalidTransactionFee"] = 155205] = "invalidTransactionFee";
    ErrorCode[ErrorCode["rejectedOnAmlScreening"] = 155206] = "rejectedOnAmlScreening";
    ErrorCode[ErrorCode["tagRequired"] = 155207] = "tagRequired";
    ErrorCode[ErrorCode["gasLimitTooLow"] = 155208] = "gasLimitTooLow";
    ErrorCode[ErrorCode["transactionDataNotEncodedProperly"] = 155209] = "transactionDataNotEncodedProperly";
    ErrorCode[ErrorCode["fullNodeReturnedError"] = 155210] = "fullNodeReturnedError";
    ErrorCode[ErrorCode["walletSetupRequired"] = 155211] = "walletSetupRequired";
    ErrorCode[ErrorCode["lowerThenMinimumAccountBalance"] = 155212] = "lowerThenMinimumAccountBalance";
    ErrorCode[ErrorCode["rejectedByBlockchain"] = 155213] = "rejectedByBlockchain";
    ErrorCode[ErrorCode["droppedAsPartOfReorg"] = 155214] = "droppedAsPartOfReorg";
    ErrorCode[ErrorCode["operationNotSupport"] = 155215] = "operationNotSupport";
    ErrorCode[ErrorCode["amountBelowMinimum"] = 155216] = "amountBelowMinimum";
    ErrorCode[ErrorCode["wrongNftTokenIdNumber"] = 155217] = "wrongNftTokenIdNumber";
    ErrorCode[ErrorCode["invalidDestinationAddress"] = 155218] = "invalidDestinationAddress";
    ErrorCode[ErrorCode["tokenWalletChainMismatch"] = 155219] = "tokenWalletChainMismatch";
    ErrorCode[ErrorCode["wrongAmountsNumber"] = 155220] = "wrongAmountsNumber";
    ErrorCode[ErrorCode["walletIsFrozen"] = 155501] = "walletIsFrozen";
    ErrorCode[ErrorCode["maxWalletLimitReached"] = 155502] = "maxWalletLimitReached";
    ErrorCode[ErrorCode["walletSetIdMutuallyExclusive"] = 155503] = "walletSetIdMutuallyExclusive";
    ErrorCode[ErrorCode["metadataUnmatched"] = 155504] = "metadataUnmatched";
    ErrorCode[ErrorCode["userCanceled"] = 155701] = "userCanceled";
    ErrorCode[ErrorCode["launchUiFailed"] = 155702] = "launchUiFailed";
    ErrorCode[ErrorCode["pinCodeNotMatched"] = 155703] = "pinCodeNotMatched";
    ErrorCode[ErrorCode["insecurePinCode"] = 155704] = "insecurePinCode";
    ErrorCode[ErrorCode["hintsMatchAnswers"] = 155705] = "hintsMatchAnswers";
    ErrorCode[ErrorCode["networkError"] = 155706] = "networkError";
    ErrorCode[ErrorCode["userSecretMissing"] = 155717] = "userSecretMissing";
    ErrorCode[ErrorCode["invalidUserTokenFormat"] = 155718] = "invalidUserTokenFormat";
    ErrorCode[ErrorCode["userTokenMismatch"] = 155719] = "userTokenMismatch";
    ErrorCode[ErrorCode["walletIdNotFound"] = 156001] = "walletIdNotFound";
    ErrorCode[ErrorCode["tokenIdNotFound"] = 156002] = "tokenIdNotFound";
    ErrorCode[ErrorCode["transactionIdNotFound"] = 156003] = "transactionIdNotFound";
    ErrorCode[ErrorCode["entityCredentialNotFound"] = 156004] = "entityCredentialNotFound";
    ErrorCode[ErrorCode["walletSetIdNotFound"] = 156005] = "walletSetIdNotFound";
})(ErrorCode = exports.ErrorCode || (exports.ErrorCode = {}));
/**
 * Social Login Provider.
 */
var SocialLoginProvider;
(function (SocialLoginProvider) {
    SocialLoginProvider["APPLE"] = "Apple";
    SocialLoginProvider["FACEBOOK"] = "Facebook";
    SocialLoginProvider["GOOGLE"] = "Google";
})(SocialLoginProvider = exports.SocialLoginProvider || (exports.SocialLoginProvider = {}));
